import { Container, Graphics, Text, Ticker } from 'pixi.js';
import { rollup, type RollupHandle } from '../utils/rollup.ts';

const COIN_COLORS = [0xffd45e, 0xffb24d, 0xffe9a8];
const COIN_COUNT = 40;

interface Coin {
  g: Graphics;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

/**
 * "BIG WIN" celebration: scrim + scale-in title + amount rollup + coin burst.
 * Board-space; awaited by the presenter so autoplay pauses for it.
 */
export class BigWinBanner {
  readonly view = new Container();
  private readonly scrim = new Graphics();
  private readonly title: Text;
  private readonly amount: Text;
  private readonly coinLayer = new Container();
  private coins: Coin[] = [];
  private activeRollup: RollupHandle | null = null;
  private readonly ticker: Ticker;
  private readonly tick = (t: Ticker) => this.updateCoins(t.deltaMS);

  constructor(boardW: number, boardH: number, ticker: Ticker, fmt: (n: number) => string) {
    this.ticker = ticker;
    this.fmt = fmt;
    this.scrim.rect(-40, -40, boardW + 80, boardH + 80).fill({ color: 0x000000, alpha: 0.55 });
    this.title = new Text({
      text: 'BIG WIN',
      style: {
        fontFamily: 'Georgia, serif',
        fontSize: 72,
        fontWeight: 'bold',
        fill: 0xffd45e,
        stroke: { color: 0x3a2405, width: 8 },
        letterSpacing: 4,
      },
    });
    this.title.anchor.set(0.5);
    this.title.position.set(boardW / 2, boardH / 2 - 34);
    this.amount = new Text({
      text: '',
      style: {
        fontFamily: 'Georgia, serif',
        fontSize: 46,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x3a2405, width: 6 },
      },
    });
    this.amount.anchor.set(0.5);
    this.amount.position.set(boardW / 2, boardH / 2 + 34);

    this.view.addChild(this.scrim, this.coinLayer, this.title, this.amount);
    this.view.visible = false;
    ticker.add(this.tick);
  }

  private fmt: (n: number) => string;

  async play(win: number): Promise<void> {
    this.view.visible = true;
    this.view.alpha = 0;
    this.amount.text = this.fmt(0);
    this.title.scale.set(0);

    // Fade scrim in + back-ease overshoot on the title.
    await this.animate(400, (t) => {
      this.view.alpha = Math.min(1, t * 2);
      const s = t < 0.7 ? (t / 0.7) * 1.15 : 1.15 - 0.15 * ((t - 0.7) / 0.3);
      this.title.scale.set(s);
    });

    this.burst();
    this.activeRollup = rollup(0, win, 1400, (v) => (this.amount.text = this.fmt(v)));
    await this.activeRollup.promise;
    this.activeRollup = null;
    this.burst();

    await this.animate(500, () => {});
    await this.animate(300, (t) => (this.view.alpha = 1 - t));
    this.view.visible = false;
  }

  cancel(): void {
    this.activeRollup?.cancel();
    this.activeRollup = null;
    this.view.visible = false;
    for (const c of this.coins) c.g.destroy();
    this.coins = [];
    this.coinLayer.removeChildren();
  }

  destroy(): void {
    this.cancel();
    this.ticker.remove(this.tick);
    this.view.destroy({ children: true });
  }

  private burst(): void {
    const cx = this.title.position.x;
    const cy = this.title.position.y + 30;
    for (let i = 0; i < COIN_COUNT; i++) {
      const g = new Graphics();
      const r = 4 + Math.random() * 3;
      g.circle(0, 0, r).fill(COIN_COLORS[(Math.random() * COIN_COLORS.length) | 0]);
      g.position.set(cx, cy);
      const ang = Math.PI * (1 + Math.random()); // upward half
      const speed = 0.25 + Math.random() * 0.35;
      this.coinLayer.addChild(g);
      this.coins.push({
        g,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed - 0.15,
        life: 0,
        maxLife: 1200 + Math.random() * 600,
      });
    }
  }

  private updateCoins(dtMs: number): void {
    if (this.coins.length === 0) return;
    const alive: Coin[] = [];
    for (const c of this.coins) {
      c.life += dtMs;
      if (c.life >= c.maxLife) {
        c.g.destroy();
        continue;
      }
      c.vy += 0.0012 * dtMs;
      c.g.x += c.vx * dtMs;
      c.g.y += c.vy * dtMs;
      c.g.alpha = 1 - c.life / c.maxLife;
      alive.push(c);
    }
    this.coins = alive;
  }

  private animate(ms: number, onFrame: (t: number) => void): Promise<void> {
    return new Promise((res) => {
      let elapsed = 0;
      const cb = (t: Ticker) => {
        elapsed += t.deltaMS;
        const p = Math.min(1, elapsed / ms);
        onFrame(p);
        if (p >= 1) {
          this.ticker.remove(cb);
          res();
        }
      };
      this.ticker.add(cb);
    });
  }
}
